<template>
  <div class="word-book-page">
    <div class="page-head">
      <h1 class="page-title">我的单词本</h1>
      <button @click="showCreateModal = true" class="btn-primary">+ 新建</button>
    </div>

    <div v-if="loading" class="state-msg">加载中...</div>
    <div v-else-if="wordBooks.length === 0" class="state-msg empty">暂无单词本，点击右上角「新建」创建</div>
    <div v-else class="book-grid">
      <div v-for="(book, index) in wordBooks" :key="book" class="book-card" @click="viewBook(book)">
        <div class="book-cover" :class="`cover-${index % 6}`">
          <div class="book-title">{{ book }}</div>
          <div class="book-words-count">{{ bookWordCounts[book] || 0 }} 个单词</div>
        </div>
        <div class="book-actions" @click.stop>
          <button @click="editBook(book)" class="btn-icon">✏️</button>
          <button @click="deleteBook(book)" class="btn-icon delete">🗑️</button>
        </div>
      </div>
    </div>

    <!-- 单词本详情弹窗 -->
    <div v-if="showDetailModal" class="modal full-screen-modal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="header-left">
            <el-button size="small" @click="closeDetailModal" round icon="el-icon-caret-left"/>
            <el-button size="small" @click="toggleWordInfo" round icon="el-icon-view"/>
            <el-button v-if="!isBatchMode" size="small" @click="exportBook" round icon="el-icon-upload2"/>
            <el-button v-if="!isBatchMode" size="small" @click="toggleFilter" round>{{ filterText }}</el-button>
            <el-button 
              v-if="filterStatus === '0'"
              size="small" 
              round 
              icon="el-icon-check"
              @click="toggleBatchMode"
              :type="isBatchMode ? 'primary' : ''"
            >
              熟词
            </el-button>
            <el-button 
              v-if="isBatchMode && selectedWords.length > 0" 
              size="small" 
              type="success" 
              round 
              :loading="submitting"
              @click="handleBatchMaster"
            >
              确认
            </el-button>
          </div>
        </div>
        <div class="modal-body book-detail-body">
          <div v-if="detailLoading" class="loading">加载中...</div>
          <div v-else-if="bookWords.length === 0" class="empty-state">
            <p>该单词本还是空的</p>
            <router-link to="/home/search" class="link-btn">去添加单词</router-link>
          </div>
          <div v-else class="word-grid">
             <div v-if="filteredBookWords.length === 0" class="empty-filter">
               <p>暂无{{ filterStatus === '0' ? '待学习' : '已掌握' }}单词</p>
             </div>
             <div 
               v-else 
               v-for="word in filteredBookWords" 
               :key="word.wordName" 
               class="word-card"
               :class="{ 'is-selected': isBatchMode && selectedWords.includes(word.wordName) }"
               @click="toggleSelection(word.wordName)"
             >
               <div class="word-card-main">
                 <div class="word-card-header">
                   <span class="word-text">{{ word.wordName }}</span>
                   <div v-if="showWordInfo && ((word.ukphone && word.ukphone.trim()) || (word.usphone && word.usphone.trim()))" class="word-phonetic">
                     <span v-if="word.ukphone && word.ukphone.trim()" class="phonetic-item">
                       <span class="phonetic-type">英</span> /{{ word.ukphone }}/
                     </span>
                     <span v-if="word.usphone && word.usphone.trim()" class="phonetic-item">
                       <span class="phonetic-type">美</span> /{{ word.usphone }}/
                     </span>
                   </div>
                 </div>
                 <div v-if="showWordInfo" class="word-card-meaning">
                   <div v-if="word.wordMeaning && typeof word.wordMeaning === 'string' && (word.wordMeaning.startsWith('{') || word.wordMeaning.startsWith('['))">
                     <div v-for="(item, index) in parseWordMeaning(word.wordMeaning)" :key="index" class="meaning-item">
                       <span class="meaning-pos" v-if="item.pos">{{ item.pos }}</span>
                       <span class="meaning-tran">{{ item.tran_cn }}</span>
                     </div>
                   </div>
                   <div v-else-if="word.wordMeaning" class="meaning-item">
                     <span class="meaning-tran">{{ word.wordMeaning }}</span>
                   </div>
                 </div>
               </div>
               <div class="word-card-actions" v-if="!isBatchMode">
                 <button @click.stop="showWordDetail(word.wordName)" class="btn-detail-small">详情</button>
                 <button @click.stop="removeWord(word.wordName)" class="btn-remove">×</button>
               </div>
               <div class="word-select-wrapper" v-if="isBatchMode" @click.stop>
                 <input type="checkbox" :value="word.wordName" v-model="selectedWords" class="word-checkbox">
               </div>
            </div>
         </div>
       </div>
     </div>
   </div>
   
    <!-- 单词详情弹窗 -->
    <WordDetailModal
      :visible.sync="showWordDetailModal"
      :word-name="detailWordName"
    />

   <!-- 创建/编辑单词本弹窗 -->
    <div v-if="showCreateModal || showEditModal" class="modal">
      <div class="modal-content small-modal">
        <div class="modal-header">
          <h3>{{ showEditModal ? '重命名单词本' : '新建单词本' }}</h3>
          <button @click="closeFormModal" class="btn-close">×</button>
        </div>
        <div class="modal-body">
          <input 
            v-model="formBookName" 
            type="text" 
            placeholder="请输入单词本名称"
            class="input-field"
          >
        </div>
        <div class="modal-footer">
          <button @click="closeFormModal" class="btn-cancel" :disabled="submitting">取消</button>
          <button @click="handleSubmit" class="btn-confirm" :disabled="submitting">{{ submitting ? '提交中...' : '确定' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getWordBooks, 
  addWordBook, 
  deleteWordBook, 
  updateBookName, 
  getBookWordsList,
  deleteWordFromBook,
  exportWordBook,
  addMasterList
} from '@/api/wordBook'
import WordDetailModal from './WordDetailModal.vue'

export default {
  name: 'WordBook',
  components: {
    WordDetailModal
  },
  data() {
    return {
      wordBooks: [],
      bookWordCounts: {},
      loading: false,
      userName: localStorage.getItem('userName'),
      
      // 详情页相关
      showDetailModal: false,
      showWordDetailModal: false,
      detailWordName: '',
      currentBookName: '',
      bookWords: [],
      detailLoading: false,
      pageNo: 1,
      pageSize: 20,
      total: 0,
      
      // 表单相关
      showCreateModal: false,
      showEditModal: false,
      formBookName: '',
      oldBookName: '',
      // 控制单词信息显示/隐藏
      showWordInfo: true,
      
      // 筛选和批量操作
      filterStatus: '0', // '0': 全部(显示wordMaster=0), '1': 熟词(显示wordMaster=1)
      selectedWords: [],
      isBatchMode: false,
      submitting: false
    }
  },
  computed: {
    filterText() {
      return this.filterStatus === '0' ? '全部' : '熟词'
    },
    filteredBookWords() {
      return this.bookWords.filter(word => word.wordMaster === this.filterStatus)
    }
  },
  mounted() {
    this.fetchBooks()
  },
  methods: {
    toggleSelection(wordName) {
      if (!this.isBatchMode) return
      
      const index = this.selectedWords.indexOf(wordName)
      if (index === -1) {
        this.selectedWords.push(wordName)
      } else {
        this.selectedWords.splice(index, 1)
      }
    },

    async fetchBooks() {
      this.loading = true
      try {
        const res = await getWordBooks(this.userName)
        if (res.data.success) {
          this.wordBooks = res.data.data || []
          // 初始化单词数量统计
          this.wordBooks.forEach(book => {
            this.fetchBookWordCount(book)
          })
        }
      } catch (error) {
        console.error('获取单词本失败', error)
      } finally {
        this.loading = false
      }
    },

    toggleWordInfo() {
      this.showWordInfo = !this.showWordInfo;
    },

    toggleFilter() {
      this.filterStatus = this.filterStatus === '0' ? '1' : '0'
      this.selectedWords = []
      this.isBatchMode = false
    },

    toggleBatchMode() {
      this.isBatchMode = !this.isBatchMode
      this.selectedWords = []
    },

    async handleBatchMaster() {
      if (this.selectedWords.length === 0 || this.submitting) return
      
      this.submitting = true
      try {
        const res = await addMasterList(this.userName, this.currentBookName, this.selectedWords)
        if (res.data.success) {
          this.$message.success('标记成功')
          this.selectedWords = []
          this.fetchBookWords()
        } else {
          this.$message.error(res.data.errMsg || '标记失败')
        }
      } catch (error) {
        console.error('标记熟词失败', error)
        this.$message.error('操作失败')
      } finally {
        this.submitting = false
      }
    },

  // 导出单词本
    async exportBook() {
      try {
        const response = await exportWordBook(this.userName, this.currentBookName)
        
        // 处理导出的文件
        const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        
        // 设置文件名，使用当前单词本名称
        const fileName = `${this.currentBookName}_单词本.txt`
        
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        
        // 清理
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('单词本导出成功！')
      } catch (error) {
        console.error('导出单词本失败:', error)
        this.$message.error('导出单词本失败，请检查网络连接或后端服务')
      }
    },
    async fetchBookWordCount(bookName) {
      try {
        const res = await getBookWordsList(this.userName, bookName, 1, 1)
        if (res.data.success) {
          this.$set(this.bookWordCounts, bookName, res.data.data.total || 0)
        }
      } catch (error) {
        console.error('获取单词数量失败', error)
      }
    },
    
    async viewBook(bookName) {
      this.currentBookName = bookName
      this.showDetailModal = true
      this.pageNo = 1
      this.fetchBookWords()
    },
    async fetchBookWords() {
      this.detailLoading = true
      try {
        const res = await getBookWordsList(this.userName, this.currentBookName, this.pageNo, this.pageSize)
        if (res.data.success) {
          this.bookWords = res.data.data.items || []
          this.total = res.data.data.total || 0
        }
      } catch (error) {
        console.error('获取单词列表失败', error)
      } finally {
        this.detailLoading = false
      }
    },
    changePage(page) {
      this.pageNo = page
      this.fetchBookWords()
    },
    async removeWord(wordName) {
      if (!confirm('确定从单词本移除该单词吗？')) return
      try {
        const res = await deleteWordFromBook(this.userName, this.currentBookName, wordName)
        if (res.data.success) {
          this.fetchBookWords()
          // 更新单词数量统计
          this.fetchBookWordCount(this.currentBookName)
        }
      } catch (error) {
        console.error('移除失败', error)
      }
    },
    closeDetailModal() {
      this.showDetailModal = false
      this.bookWords = []
      this.currentBookName = ''
    },
    showWordDetail(wordName) {
      this.detailWordName = wordName
      this.showWordDetailModal = true
    },
    
    // 单词本管理
    editBook(bookName) {
      this.oldBookName = bookName
      this.formBookName = bookName
      this.showEditModal = true
    },
    async deleteBook(bookName) {
      if (!confirm(`确定要删除单词本 "${bookName}" 吗？`)) return
      try {
        const res = await deleteWordBook(this.userName, bookName)
        if (res.data.success) {
          this.fetchBooks()
          // 从单词数量统计中移除
          this.$delete(this.bookWordCounts, bookName)
        } else {
          this.$message.error('删除失败')
        }
      } catch (error) {
        console.error('删除失败', error)
        this.$message.error('删除失败')
      }
    },
    async handleSubmit() {
      if (!this.formBookName.trim() || this.submitting) return
      
      this.submitting = true
      try {
        if (this.showEditModal) {
          const res = await updateBookName(this.userName, this.oldBookName, this.formBookName)
          if (res.data.success) {
            // 更新单词数量统计的键名
            const count = this.bookWordCounts[this.oldBookName]
            if (count) {
              this.$set(this.bookWordCounts, this.formBookName, count)
              this.$delete(this.bookWordCounts, this.oldBookName)
            }
            this.fetchBooks()
            this.closeFormModal()
          } else {
            this.$message.error(res.data.errMsg)
          }
        } else {
          const res = await addWordBook(this.userName, { bookName: this.formBookName })
          if (res.data.success) {
            // 初始化新单词本的数量统计
            this.$set(this.bookWordCounts, this.formBookName, 0)
            this.fetchBooks()
            this.closeFormModal()
          } else {
            this.$message.error(res.data.errMsg)
          }
        }
      } catch (error) {
        console.error('操作失败', error)
        this.$message.error('操作失败')
      } finally {
        this.submitting = false
      }
    },
    closeFormModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.formBookName = ''
      this.oldBookName = ''
    },
    
    // 解析单词释义
    parseWordMeaning(meaning) {
      try {
        const parsed = JSON.parse(meaning)
        return Array.isArray(parsed) ? parsed : [parsed]
      } catch (e) {
        return []
      }
    }
  }
}
</script>

<style scoped>
.word-book-page {
  padding: 48px 24px 64px;
  max-width: 1100px;
  margin: 0 auto;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.03em;
}

.btn-primary {
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: var(--radius);
  font-weight: 600;
  font-size: 0.95rem;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: var(--accent-hover);
}

.book-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 24px;
}

.state-msg {
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-muted);
}

.state-msg.empty {
  padding: 64px 20px;
  font-size: 1.05rem;
}

.book-card {
  position: relative;
  perspective: 1000px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.book-card:hover {
  transform: translateY(-6px);
}

.book-cover {
  height: 220px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #fff;
  font-weight: 700;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

/* 参考 Quizlet 多彩卡片 */
.cover-0 {
  background: linear-gradient(145deg, #e85d75 0%, #f093a0 100%);
}
.cover-1 {
  background: linear-gradient(145deg, #6ccec2 0%, #8ed9d0 100%);
}
.cover-2 {
  background: linear-gradient(145deg, #7c7ce0 0%, #a8a8f0 100%);
}
.cover-3 {
  background: linear-gradient(145deg, #f59e0b 0%, #fbbf24 100%);
}
.cover-4 {
  background: linear-gradient(145deg, #ec4899 0%, #f472b6 100%);
}
.cover-5 {
  background: linear-gradient(145deg, #3b82f6 0%, #60a5fa 100%);
}

.book-cover::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  opacity: 0;
}

.book-cover:hover::before {
  opacity: 1;
}

.book-cover:hover {
  transform: rotateY(-5deg) rotateX(5deg);
  box-shadow: var(--shadow-lg);
}

.book-title {
  font-size: 1.2rem;
  margin-bottom: 10px;
  z-index: 1;
  line-height: 1.35;
}

.book-words-count {
  font-size: 0.9rem;
  opacity: 0.95;
  z-index: 1;
  font-weight: 600;
  background: rgba(255,255,255,0.25);
  padding: 5px 14px;
  border-radius: var(--radius-round);
}

.book-actions {
  position: absolute;
  bottom: 15px;
  right: 15px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  display: flex;
  gap: 10px;
}

.book-card:hover .book-actions {
  opacity: 1;
}

.btn-icon {
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 1.1rem;
}

.btn-icon:hover {
  transform: scale(1.15);
  box-shadow: var(--shadow-md);
}

.btn-icon.delete:hover {
  background: var(--danger-color);
  color: white;
}

/* 模态框样式 */
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1100;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.full-screen-modal .modal-content {
  width: 100%;
  height: 100%;
  max-width: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface);
}

.small-modal {
  width: 90%;
  max-width: 400px;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-card);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Element UI Button Overrides */
.el-button--small.is-round {
  padding: 9px 15px;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
  font-weight: 700;
}

.book-detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: var(--surface);
}

/* 单词本内单词：网格卡片，与查单词页一致 */
.word-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  max-width: 1100px;
  margin: 0 auto;
}

.word-card {
  background: var(--surface-card);
  padding: 22px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.word-card.is-selected {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.word-card:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
}

.word-card-main {
  flex: 1;
  min-width: 0;
}

.word-card-header {
  margin-bottom: 10px;
}

.word-card-header .word-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--ink);
  display: block;
  margin-bottom: 6px;
}

.word-card-meaning {
  color: var(--ink-secondary);
  line-height: 1.55;
  font-size: 0.95rem;
  margin-bottom: 16px;
}

.word-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

.phonetic-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.phonetic-type {
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #666;
}

.word-meaning {
  color: var(--text-color);
  line-height: 1.6;
}

.meaning-item {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  align-items: baseline;
}

.meaning-pos {
  background: var(--accent);
  color: #fff;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.meaning-tran {
  flex: 1;
  min-width: 0;
  font-size: 1rem;
}

.btn-remove {
  background: #fff0f0;
  border: none;
  color: var(--danger-color);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
}

.btn-remove:hover {
  color: white;
  background: var(--danger-color);
  transform: rotate(90deg);
}

.btn-detail-small {
  background: white;
  border: 1px solid var(--secondary-color);
  color: var(--secondary-color);
  padding: 5px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
}

.btn-detail-small:hover {
  background: var(--secondary-color);
  color: white;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  text-align: right;
  background: white;
}

.input-field {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: var(--bg-color);
}

.input-field:focus {
  outline: none;
  border-color: var(--accent);
  background: #fff;
  box-shadow: 0 0 0 3px var(--accent-soft);
}

.modal-body {
  padding: 24px;
}

.btn-cancel, .btn-confirm {
  padding: 10px 24px;
  border-radius: var(--border-radius-round);
  border: none;
  cursor: pointer;
  margin-left: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
  font-size: 0.95rem;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
  color: #333;
}

.btn-confirm {
  background: var(--accent);
  color: #fff;
}

.btn-confirm:hover {
  background: var(--accent-hover);
}

.word-card .word-phonetic {
  margin-top: 4px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #ccc;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  line-height: 1;
}

.btn-close:hover {
  color: var(--danger-color);
  transform: rotate(90deg);
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-muted);
}

.empty-state p {
  font-size: 1rem;
  margin-bottom: 16px;
}

.link-btn {
  color: var(--accent);
  text-decoration: none;
  font-weight: 600;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  background: transparent;
  padding: 0;
  border: none;
  display: inline;
}

.link-btn:hover {
  border-bottom-color: var(--accent);
  color: var(--accent);
  background: transparent;
}

.loading {
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-muted);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@media (max-width: 768px) {
  .book-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
    padding: 0 15px;
  }
  
  .book-cover {
    height: 180px;
    padding: 15px;
  }
  
  .book-title {
    font-size: 1.1rem;
  }
  
  .header-left {
    gap: 5px;
  }
  
  .btn-remove {
    margin-left: 10px;
  }
  
  .word-grid {
    grid-template-columns: 1fr;
  }
}

.word-select-wrapper {
  display: flex;
  align-items: center;
  margin-left: 12px;
}

.word-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--primary-color);
}

.empty-filter {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 1rem;
  background: rgba(255,255,255,0.5);
  border-radius: var(--border-radius-md);
  margin: 20px 0;
  border: 2px dashed #eee;
}
</style>